/**
 * Create Audience Modal
 * Story: E007-S027 Audience Manager & Targeting Optimization
 */

import React, { useState, useEffect } from 'react';
import { useCreateAudience, useEstimateAudienceSize, suggestInterests, suggestLocations } from '../../lib/audiencesApi';
import type { Platform, AudienceCriteria, CreateAudienceRequest, LocationSuggestion } from '../../types/audiences';
import { formatAudienceSize, PLATFORM_INFO } from '../../types/audiences';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateAudienceModal: React.FC<Props> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState<CreateAudienceRequest>({
    name: '',
    description: '',
    platform: 'meta',
    type: 'custom',
    criteria: {
      demographics: {
        age_min: 18,
        age_max: 65,
        genders: ['all'],
        locations: ['US'],
      },
      interests: [],
      behaviors: [],
    },
  });

  const [interestQuery, setInterestQuery] = useState('');
  const [interestSuggestions, setInterestSuggestions] = useState<string[]>([]);
  const [locationQuery, setLocationQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState<LocationSuggestion[]>([]);
  const [estimatedSize, setEstimatedSize] = useState<number | null>(null);

  const createAudience = useCreateAudience();
  const estimateSize = useEstimateAudienceSize();

  // Fetch interest suggestions
  useEffect(() => {
    if (interestQuery.length >= 2) {
      const timer = setTimeout(async () => {
        try {
          const result = await suggestInterests(interestQuery, formData.platform);
          setInterestSuggestions(result.suggestions);
        } catch (e) {
          console.error('Failed to fetch interests:', e);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setInterestSuggestions([]);
    }
  }, [interestQuery, formData.platform]);

  // Fetch location suggestions
  useEffect(() => {
    if (locationQuery.length >= 2) {
      const timer = setTimeout(async () => {
        try {
          const result = await suggestLocations(locationQuery);
          setLocationSuggestions(result.suggestions);
        } catch (e) {
          console.error('Failed to fetch locations:', e);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setLocationSuggestions([]);
    }
  }, [locationQuery]);

  // Estimate audience size on criteria change
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const result = await estimateSize.mutateAsync({
          platform: formData.platform,
          criteria: formData.criteria,
        });
        setEstimatedSize(result.estimated_size);
      } catch (e) {
        console.error('Failed to estimate size:', e);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.platform, formData.criteria]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createAudience.mutateAsync(formData);
      onSuccess();
    } catch (error) {
      console.error('Failed to create audience:', error);
    }
  };

  const addInterest = (interest: string) => {
    if (!formData.criteria?.interests?.includes(interest)) {
      setFormData({
        ...formData,
        criteria: {
          ...formData.criteria,
          interests: [...(formData.criteria?.interests || []), interest],
        },
      });
    }
    setInterestQuery('');
    setInterestSuggestions([]);
  };

  const removeInterest = (interest: string) => {
    setFormData({
      ...formData,
      criteria: {
        ...formData.criteria,
        interests: formData.criteria?.interests?.filter((i) => i !== interest) || [],
      },
    });
  };

  const addLocation = (location: LocationSuggestion) => {
    if (!formData.criteria?.demographics?.locations?.includes(location.code)) {
      setFormData({
        ...formData,
        criteria: {
          ...formData.criteria,
          demographics: {
            ...formData.criteria?.demographics,
            locations: [...(formData.criteria?.demographics?.locations || []), location.code],
          },
        },
      });
    }
    setLocationQuery('');
    setLocationSuggestions([]);
  };

  const removeLocation = (locationCode: string) => {
    setFormData({
      ...formData,
      criteria: {
        ...formData.criteria,
        demographics: {
          ...formData.criteria?.demographics,
          locations: formData.criteria?.demographics?.locations?.filter((l) => l !== locationCode) || [],
        },
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Create New Audience
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Audience Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="e.g., High-Value Customers - US"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Brief description of this audience..."
              rows={2}
            />
          </div>

          {/* Platform */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Platform *
            </label>
            <div className="flex gap-3">
              {(['meta', 'google', 'tiktok'] as Platform[]).map((platform) => (
                <button
                  key={platform}
                  type="button"
                  onClick={() => setFormData({ ...formData, platform })}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                    formData.platform === platform
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="font-medium text-gray-900 dark:text-white">
                    {PLATFORM_INFO[platform].name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Demographics */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
              Demographics
            </h3>

            {/* Age Range */}
            <div className="flex gap-4 mb-4">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Min Age</label>
                <input
                  type="number"
                  min={13}
                  max={65}
                  value={formData.criteria?.demographics?.age_min || 18}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      criteria: {
                        ...formData.criteria,
                        demographics: {
                          ...formData.criteria?.demographics,
                          age_min: parseInt(e.target.value),
                        },
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Max Age</label>
                <input
                  type="number"
                  min={13}
                  max={65}
                  value={formData.criteria?.demographics?.age_max || 65}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      criteria: {
                        ...formData.criteria,
                        demographics: {
                          ...formData.criteria?.demographics,
                          age_max: parseInt(e.target.value),
                        },
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Gender */}
            <div className="mb-4">
              <label className="block text-xs text-gray-500 mb-2">Gender</label>
              <div className="flex gap-2">
                {[
                  { value: 'all', label: 'All' },
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' },
                ].map((option) => (
                  <label key={option.value} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.criteria?.demographics?.genders?.includes(option.value as any)}
                      onChange={(e) => {
                        const current = formData.criteria?.demographics?.genders || [];
                        const updated = e.target.checked
                          ? [...current, option.value]
                          : current.filter((g) => g !== option.value);
                        setFormData({
                          ...formData,
                          criteria: {
                            ...formData.criteria,
                            demographics: {
                              ...formData.criteria?.demographics,
                              genders: updated as any[],
                            },
                          },
                        });
                      }}
                      className="rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Locations */}
            <div>
              <label className="block text-xs text-gray-500 mb-2">Locations</label>
              <div className="relative">
                <input
                  type="text"
                  value={locationQuery}
                  onChange={(e) => setLocationQuery(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Search locations..."
                />
                {locationSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                    {locationSuggestions.map((loc) => (
                      <button
                        key={loc.code}
                        type="button"
                        onClick={() => addLocation(loc)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 text-sm"
                      >
                        {loc.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.criteria?.demographics?.locations?.map((loc) => (
                  <span
                    key={loc}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-xs"
                  >
                    {loc}
                    <button type="button" onClick={() => removeLocation(loc)} className="hover:text-blue-900">
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Interests */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Interests
            </label>
            <div className="relative">
              <input
                type="text"
                value={interestQuery}
                onChange={(e) => setInterestQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Search interests..."
              />
              {interestSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {interestSuggestions.map((interest) => (
                    <button
                      key={interest}
                      type="button"
                      onClick={() => addInterest(interest)}
                      className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 text-sm"
                    >
                      {interest}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.criteria?.interests?.map((interest) => (
                <span
                  key={interest}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-full text-xs"
                >
                  {interest}
                  <button type="button" onClick={() => removeInterest(interest)} className="hover:text-green-900">
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Estimated Size */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Estimated Reach:</span>
              <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                {estimatedSize ? `~${formatAudienceSize(estimatedSize)} people` : 'Calculating...'}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formData.name || createAudience.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {createAudience.isPending && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              Create Audience
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
